import React from 'react';

import { DateObject } from '@uplift/services';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MobileCalendarDayProps } from '../shared';


const useCalendarDayStyles = makeStyles<void, 'root'>({ name: 'CalendarDay' })((theme, _params, classes) => ({
  root: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    cursor: 'pointer',
  },
  rootOtherMonth: {
    opacity: 0.4,
  },
  rootDisabled: {
    opacity: 0.4,
    cursor: 'default',
  },
  rootSelected: {
    opacity: 1,
  },

  date: {
    width: '26px',
    height: '26px',
    lineHeight: '28px',
    textAlign: 'center',
    borderRadius: '13px',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.up('sm')]: {
      [`.${classes.root}:hover &`]: {
        backgroundColor: theme.palette.secondary.background,
      }
    }
  },
  currentMonth: {
    color: theme.palette.text.primary,
  },
  current: {
    color: theme.palette.secondary.main,
    fontWeight: 600
  },
  selected: {
    backgroundColor: '#643C9D4D',
    color: theme.palette.text.primary,

    [theme.breakpoints.up('sm')]: {
      [`.${classes.root}:hover &`]: {
        backgroundColor: '#643C9D4D',
      }
    }
  },
}));


export const useDayInfo = (selectedDate: DateObject, day: DateObject, today: DateObject, currentMonth: DateObject) => {
  return React.useMemo(() => ({
    past: day.isBefore(today, 'day'),
    today: day.isSame(today, 'day'),
    selected: day.isSame(selectedDate, 'day'),
    currentMonth: day.isSame(currentMonth, 'month'),
  }), [ selectedDate, day, today, currentMonth ]);
};


export const MobileCalendarDay: React.FC<MobileCalendarDayProps> = ({
  selectedDate,
  date,
  today,
  currentMonth,
  onSelect,
  disablePastDays,
  children,
}) => {
  const { classes, cx } = useCalendarDayStyles();
  const dateInfo = useDayInfo(selectedDate, date, today, currentMonth);
  const disabled = disablePastDays && dateInfo.past;

  const rootClassName = cx(classes.root, {
    [classes.rootOtherMonth]: !dateInfo.currentMonth,
    [classes.rootDisabled]: disabled,
    [classes.rootSelected]: dateInfo.selected,
  });

  const labelClassName = cx(classes.date, {
    [classes.current]: dateInfo.today,
    [classes.selected]: dateInfo.selected,
    [classes.currentMonth]: dateInfo.currentMonth,
  });

  return (
    <Box
      className={rootClassName}
      onClick={disabled ? undefined : () => onSelect(date)}
    >
      <Typography className={labelClassName}>
        {date.format('D')}
      </Typography>
      {children}
    </Box>
  );
};
